import React, { useCallback, useEffect, useState, useRef } from 'react';
import { ChevronDownIcon, XIcon, PlusIcon } from 'lucide-react';
import { createPortal } from 'react-dom';
interface FeatureField {
  id: string;
  label: string;
  placeholder: string;
  value: string;
}
interface FeatureTemplateProps {
  sectionId: string;
  personaOptions?: {
    id: string;
    name: string;
  }[];
  selectedPersonaIds?: string[];
  onPersonaSelectionChange?: (ids: string[]) => void;
  fields?: Record<string, string>;
  onFieldsChange?: (fields: Record<string, string>) => void;
  onAddPersona?: (name: string) => void;
}
const DEFAULT_FIELDS: Omit<FeatureField, 'value'>[] = [{
  id: 'description',
  label: 'Description',
  placeholder: 'Describe what this feature does and the problem it solves…'
}, {
  id: 'strategic-goals',
  label: 'Strategic Goals',
  placeholder: 'What business or product goals does this feature support?'
}, {
  id: 'expected-usage',
  label: 'Expected Usage',
  placeholder: 'How and how often will users interact with this feature?'
}];
const PERSONA_TRAIT_LABELS = ['Sensitivity to Time', 'Cognitive Load', 'Persistence', 'Curiosity', 'Tech Proficiency', 'Intuition'];
const PERSONA_RADAR_LABELS = ['Time Sensitivity', 'Cognitive Load', 'Persistence', 'Curiosity', 'Tech Proficiency', 'Intuition'];
const PERSONA_OCCUPATION_SUGGESTIONS = ['Software Engineer', 'Product Manager', 'Designer', 'Marketing Specialist', 'Sales Professional', 'Teacher', 'Healthcare Worker', 'Entrepreneur', 'Student', 'Consultant'];
function ModalTraitSlider({
  label,
  value,
  onChange
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
}) {
  return <div className="mb-4" data-id="element-192">
      <div className="flex items-center justify-between mb-1.5" data-id="element-193">
        <span className="text-xs font-medium text-stone-700" data-id="element-194">{label}</span>
        <span className="text-[10px] text-stone-400" data-id="element-195">{value.toFixed(0)}</span>
      </div>
      <input type="range" min={0} max={100} value={value} onChange={e => onChange(Number(e.target.value))} className="w-full h-1 bg-stone-200 rounded-full appearance-none cursor-pointer accent-stone-800 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-stone-800 [&::-webkit-slider-thumb]:cursor-pointer [&::-moz-range-thumb]:w-3 [&::-moz-range-thumb]:h-3 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-stone-800 [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:cursor-pointer" data-id="element-196" />
    </div>;
}
function ModalRadarChart({
  data
}: {
  data: {
    label: string;
    value: number;
  }[];
}) {
  const cx = 150;
  const cy = 125;
  const maxR = 80;
  const levels = 4;
  const n = data.length;
  const angleStep = 2 * Math.PI / n;
  const startAngle = -Math.PI / 2;
  const getPoint = (index: number, radius: number) => {
    const angle = startAngle + index * angleStep;
    return {
      x: cx + radius * Math.cos(angle),
      y: cy + radius * Math.sin(angle)
    };
  };
  const gridPaths = Array.from({
    length: levels
  }, (_, level) => {
    const r = maxR * (level + 1) / levels;
    const points = Array.from({
      length: n
    }, (_, i) => {
      const p = getPoint(i, r);
      return `${p.x},${p.y}`;
    }).join(' ');
    return <polygon key={level} points={points} fill="none" stroke="#d6d3d1" strokeWidth={0.75} data-id="element-197" />;
  });
  const axisLines = Array.from({
    length: n
  }, (_, i) => {
    const p = getPoint(i, maxR);
    return <line key={i} x1={cx} y1={cy} x2={p.x} y2={p.y} stroke="#d6d3d1" strokeWidth={0.75} data-id="element-198" />;
  });
  const dataPoints = data.map((d, i) => {
    const r = d.value / 100 * maxR;
    const p = getPoint(i, r);
    return `${p.x},${p.y}`;
  }).join(' ');
  const labels = data.map((d, i) => {
    const labelR = maxR + 18;
    const p = getPoint(i, labelR);
    const angle = startAngle + i * angleStep;
    let textAnchor: 'start' | 'middle' | 'end' = 'middle';
    if (Math.cos(angle) > 0.3) textAnchor = 'start';else if (Math.cos(angle) < -0.3) textAnchor = 'end';
    return <text key={i} x={p.x} y={p.y} textAnchor={textAnchor} dominantBaseline="central" fontSize={9} fill="#78716c" data-id="element-199">
        {d.label}
      </text>;
  });
  return <svg viewBox="0 0 300 250" className="w-full h-full" overflow="visible" data-id="element-200">
      {gridPaths}
      {axisLines}
      <polygon points={dataPoints} fill="#78716c" fillOpacity={0.3} stroke="#44403c" strokeWidth={1.5} data-id="element-201" />
      {labels}
    </svg>;
}
function PersonaMultiSelect({
  options,
  selectedIds,
  onChangeIds
}: {
  options: {
    id: string;
    name: string;
  }[];
  selectedIds: string[];
  onChangeIds: (ids: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const toggle = (id: string) => {
    if (selectedIds.includes(id)) {
      onChangeIds(selectedIds.filter(s => s !== id));
    } else {
      onChangeIds([...selectedIds, id]);
    }
  };
  const remove = (id: string) => {
    onChangeIds(selectedIds.filter(s => s !== id));
  };
  const availableOptions = options.filter(o => o.name.trim());
  return <div ref={containerRef} className="relative" data-id="element-202">
      <button type="button" onClick={() => setOpen(!open)} className={`w-full text-left text-sm rounded-none px-3 py-2.5 border bg-stone-50/50 min-h-[2.75em] focus:border-stone-300 focus:bg-white transition-colors duration-100 flex items-center gap-2 ${selectedIds.length > 0 ? 'border-stone-200 text-stone-700' : 'border-stone-200 text-stone-400'}`} data-id="element-203">
        <div className="flex-1 flex flex-wrap gap-1.5" data-id="element-204">
          {selectedIds.length > 0 ? selectedIds.map(id => {
          const opt = options.find(o => o.id === id);
          const displayName = opt?.name || 'Unknown';
          return <span key={id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-none bg-stone-200/70 text-stone-700 text-xs font-medium" data-id="element-205">
                  {displayName}
                  <span role="button" tabIndex={0} onClick={e => {
              e.stopPropagation();
              remove(id);
            }} onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                remove(id);
              }
            }} className="text-stone-400 hover:text-stone-600 transition-colors" data-id="element-206">
                    <XIcon className="w-3 h-3" data-id="element-207" />
                  </span>
                </span>;
        }) : <span data-id="element-208">Select target personas…</span>}
        </div>
        <ChevronDownIcon className={`w-4 h-4 text-stone-400 flex-shrink-0 transition-transform duration-150 ${open ? 'rotate-180' : ''}`} data-id="element-209" />
      </button>

      {open && <div className="absolute z-10 mt-1 w-full bg-white border border-stone-200 rounded-none shadow-lg py-1 max-h-48 overflow-y-auto" data-id="element-210">
          {availableOptions.length === 0 ? <div className="px-3 py-2.5 text-sm text-stone-400 italic" data-id="element-211">
              No personas defined yet. Add personas in the Persona Hub.
            </div> : availableOptions.map(option => {
        const isSelected = selectedIds.includes(option.id);
        return <button key={option.id} type="button" onClick={() => toggle(option.id)} className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2.5 transition-colors duration-75 ${isSelected ? 'bg-stone-100 text-stone-900' : 'text-stone-600 hover:bg-stone-50'}`} data-id="element-212">
                  <span className={`w-4 h-4 rounded-none border flex-shrink-0 flex items-center justify-center transition-colors duration-75 ${isSelected ? 'bg-stone-800 border-stone-800' : 'border-stone-300'}`} data-id="element-213">
                    {isSelected && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3} data-id="element-214">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" data-id="element-215" />
                      </svg>}
                  </span>
                  <span data-id="element-216">{option.name}</span>
                </button>;
      })}
        </div>}
    </div>;
}
export function FeatureTemplate({
  sectionId,
  personaOptions = [],
  selectedPersonaIds = [],
  onPersonaSelectionChange,
  fields = {},
  onFieldsChange,
  onAddPersona
}: FeatureTemplateProps) {
  const [localFields, setLocalFields] = useState<Record<string, string>>({});
  const [showAddPersonaModal, setShowAddPersonaModal] = useState(false);
  const [newPersonaName, setNewPersonaName] = useState('');
  const [newPersonaOccupation, setNewPersonaOccupation] = useState('');
  const [newPersonaTraits, setNewPersonaTraits] = useState<Record<string, number>>({
    'Sensitivity to Time': 50,
    'Cognitive Load': 50,
    Persistence: 50,
    Curiosity: 50,
    'Tech Proficiency': 50,
    Intuition: 50
  });
  const currentFields = onFieldsChange ? fields : localFields;
  const handleInput = useCallback((fieldId: string, value: string) => {
    if (onFieldsChange) {
      onFieldsChange({
        ...fields,
        [fieldId]: value
      });
    } else {
      setLocalFields(prev => ({
        ...prev,
        [fieldId]: value
      }));
    }
  }, [fields, onFieldsChange]);
  const radarData = PERSONA_TRAIT_LABELS.map((label, i) => ({
    label: PERSONA_RADAR_LABELS[i],
    value: newPersonaTraits[label]
  }));
  const handleSavePersona = () => {
    const name = newPersonaName.trim();
    if (!name || !onAddPersona) return;
    onAddPersona(name);
    setNewPersonaName('');
    setNewPersonaOccupation('');
    setNewPersonaTraits({
      'Sensitivity to Time': 50,
      'Cognitive Load': 50,
      Persistence: 50,
      Curiosity: 50,
      'Tech Proficiency': 50,
      Intuition: 50
    });
    setShowAddPersonaModal(false);
  };
  const handleClosePersonaModal = () => {
    setShowAddPersonaModal(false);
    setNewPersonaName('');
    setNewPersonaOccupation('');
    setNewPersonaTraits({
      'Sensitivity to Time': 50,
      'Cognitive Load': 50,
      Persistence: 50,
      Curiosity: 50,
      'Tech Proficiency': 50,
      Intuition: 50
    });
  };
  return <div className="mt-6 space-y-5" data-id="element-217">
      {DEFAULT_FIELDS.map(field => {
      const value = currentFields[field.id] ?? '';
      const isEmpty = !value.trim();
      return <div key={field.id} data-id="element-218">
            <label className="block text-xs font-semibold text-stone-400 uppercase tracking-wider mb-1.5" data-id="element-219">
              {field.label}
            </label>
            <div contentEditable suppressContentEditableWarning className={`w-full text-sm outline-none rounded-none px-3 py-2.5 border border-stone-200 bg-stone-50/50 min-h-[3em] focus:border-stone-300 focus:bg-white transition-colors duration-100 ${isEmpty ? 'text-stone-400' : 'text-stone-700'}`} data-placeholder={field.placeholder} onInput={e => {
          handleInput(field.id, (e.target as HTMLDivElement).textContent ?? '');
        }} onFocus={e => {
          const el = e.target as HTMLDivElement;
          if (isEmpty && el.textContent === field.placeholder) {
            el.textContent = '';
          }
        }} onBlur={e => {
          const el = e.target as HTMLDivElement;
          if (!el.textContent?.trim()) {
            el.textContent = field.placeholder;
          }
        }} ref={el => {
          if (el) {
            if (isEmpty && !el.textContent) {
              el.textContent = field.placeholder;
            } else if (!isEmpty && el.textContent !== value) {
              el.textContent = value;
            }
          }
        }} data-id="element-220" />
          </div>;
    })}
      {/* Target Personas */}
      <div data-id="element-221">
        <label className="block text-xs font-semibold text-stone-400 uppercase tracking-wider mb-1.5" data-id="element-222">
          Target Personas
        </label>
        {personaOptions.length > 0 ? <>
            <PersonaMultiSelect options={personaOptions} selectedIds={selectedPersonaIds} onChangeIds={onPersonaSelectionChange || (() => {})} data-id="element-223" />
            {onAddPersona && <button type="button" onClick={() => setShowAddPersonaModal(true)} className="inline-flex items-center gap-1 mt-2.5 text-xs text-stone-400 hover:text-stone-600 transition-colors duration-100" data-id="element-224">
                <PlusIcon className="w-3 h-3" data-id="element-225" />
                <span data-id="element-226">Add a Persona</span>
              </button>}
          </> : <div className="flex items-center justify-between pt-1" data-id="element-227">
            <p className="text-sm text-stone-400 italic" data-id="element-228">
              No personas defined yet.
            </p>
            {onAddPersona && <button type="button" onClick={() => setShowAddPersonaModal(true)} className="inline-flex items-center gap-1 text-sm text-stone-500 hover:text-stone-700 transition-colors duration-100 flex-shrink-0" data-id="element-229">
                <PlusIcon className="w-3.5 h-3.5" data-id="element-230" />
                <span data-id="element-231">Add a Persona</span>
              </button>}
          </div>}
      </div>
      {/* Add Persona Modal — Full Template */}
      {showAddPersonaModal && createPortal(<div className="fixed inset-0 z-[9999] flex items-center justify-center" data-id="element-232">
            <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={handleClosePersonaModal} data-id="element-233" />
            <div className="relative bg-white rounded-none shadow-2xl border border-stone-200 w-full max-w-3xl mx-4 max-h-[90vh] overflow-y-auto z-10" data-id="element-234">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-stone-200 sticky top-0 bg-white z-10 rounded-none" data-id="element-235">
                <h2 className="text-lg font-semibold text-stone-900" data-id="element-236">
                  New Persona
                </h2>
                <button type="button" onClick={handleClosePersonaModal} className="w-7 h-7 flex items-center justify-center rounded-none text-stone-400 hover:text-stone-600 hover:bg-stone-100 transition-colors duration-100" aria-label="Close" data-id="element-237">
                  <XIcon className="w-4 h-4" data-id="element-238" />
                </button>
              </div>

              {/* Body */}
              <div className="px-6 py-5 space-y-6" data-id="element-239">
                {/* Persona Name */}
                <div data-id="element-240">
                  <label className="block text-xs font-semibold text-stone-400 uppercase tracking-wider mb-1.5" data-id="element-241">
                    Persona Name
                  </label>
                  <input type="text" value={newPersonaName} onChange={e => setNewPersonaName(e.target.value)} placeholder="Enter persona name…" className="w-full text-sm outline-none rounded-none px-3 py-2.5 border border-stone-200 bg-stone-50/50 focus:border-stone-300 focus:bg-white transition-colors duration-100 placeholder:text-stone-400" autoFocus data-id="element-242" />
                </div>

                {/* Occupation */}
                <div data-id="element-243">
                  <label className="block text-xs font-semibold text-stone-400 uppercase tracking-wider mb-1.5" data-id="element-244">
                    Occupation
                  </label>
                  <input type="text" value={newPersonaOccupation} onChange={e => setNewPersonaOccupation(e.target.value)} placeholder="Enter occupation or select below" className="w-full text-sm outline-none rounded-none px-3 py-2.5 border border-stone-200 bg-stone-50/50 focus:border-stone-300 focus:bg-white transition-colors duration-100 placeholder:text-stone-400 mb-3" data-id="element-245" />
                  <p className="text-[10px] text-stone-400 uppercase tracking-wider font-semibold mb-2" data-id="element-246">
                    Suggestions
                  </p>
                  <div className="flex flex-wrap gap-1.5" data-id="element-247">
                    {PERSONA_OCCUPATION_SUGGESTIONS.map(occ => <button key={occ} type="button" onClick={() => setNewPersonaOccupation(occ)} className={`px-2.5 py-1 text-xs rounded-none border transition-colors duration-100 ${newPersonaOccupation === occ ? 'bg-stone-800 text-white border-stone-800' : 'bg-white text-stone-600 border-stone-300 hover:border-stone-400'}`} data-id="element-248">
                        {occ}
                      </button>)}
                  </div>
                </div>

                {/* Personality Traits + Radar */}
                <div className="border-t border-stone-200 pt-5" data-id="element-249">
                  <label className="block text-xs font-semibold text-stone-400 uppercase tracking-wider mb-4" data-id="element-250">
                    Personality Traits
                  </label>
                  <div className="flex gap-6" data-id="element-251">
                    {/* Sliders */}
                    <div className="flex-1 min-w-0" data-id="element-252">
                      {PERSONA_TRAIT_LABELS.map(label => <ModalTraitSlider key={label} label={label} value={newPersonaTraits[label]} onChange={v => setNewPersonaTraits(prev => ({
                  ...prev,
                  [label]: v
                }))} data-id="element-253" />)}
                    </div>

                    {/* Radar Preview */}
                    <div className="w-56 flex-shrink-0" data-id="element-254">
                      <div className="border border-stone-200 rounded-none p-3 bg-stone-50/50" data-id="element-255">
                        <p className="text-[10px] font-semibold text-stone-500 uppercase tracking-wider mb-2" data-id="element-256">
                          Preview
                        </p>
                        <div className="h-44" data-id="element-257">
                          <ModalRadarChart data={radarData} data-id="element-258" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-stone-200 flex justify-end gap-2 sticky bottom-0 bg-white rounded-none" data-id="element-259">
                <button type="button" onClick={handleClosePersonaModal} className="px-4 py-2 text-sm font-medium text-stone-600 rounded-none border border-stone-200 hover:bg-stone-50 transition-colors duration-100" data-id="element-260">
                  Cancel
                </button>
                <button type="button" onClick={handleSavePersona} disabled={!newPersonaName.trim()} className="px-4 py-2 text-sm font-medium text-white bg-stone-800 rounded-none hover:bg-stone-700 transition-colors duration-100 disabled:opacity-40 disabled:cursor-not-allowed" data-id="element-261">
                  Save Persona
                </button>
              </div>
            </div>
          </div>, document.body)}
    </div>;
}